import * as React from 'react';
import toNumber from 'lodash/toNumber';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext, { ngettext } from '@sdk/lang/gettext';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import displayNumberOrReplacer from '@sdk/utils/displayNumberOrReplacer';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import SettingsSection from '../../../settings/components/SettingsSection';
import SettingsSwitcherRow from '../../../settings/components/SettingsSwitcherRow';
import { FIELD_WIDTH_100 } from '../../../settings/constants';
import { CustomTextField } from '../components/CustomTextField';

import type {
  OvertimeEnabledField,
  OvertimeWeekMultiplierField,
  OvertimeWeekThresholdField,
} from './types/LaborCostFields';

type Props = {
  overtimeEnabledField: OvertimeEnabledField;
  overtimeWeekThresholdField: OvertimeWeekThresholdField;
  overtimeWeekMultiplierField: OvertimeWeekMultiplierField;
};

const weeklyOvertime: React.FC<React.PropsWithChildren<Props>> = ({
  overtimeEnabledField,
  overtimeWeekThresholdField,
  overtimeWeekMultiplierField,
}) => {
  const onChangeHandler = (value: boolean): void => {
    if (!value) {
      overtimeWeekThresholdField.dirty ? overtimeWeekThresholdField.reset() : null;
      overtimeWeekMultiplierField.dirty ? overtimeWeekMultiplierField.reset() : null;
    }

    overtimeEnabledField.onChange(value);
  };

  return (
    <SettingsSection withSeparator>
      <CollapsibleSection
        isActive={overtimeEnabledField.value}
        headerItem={
          <SettingsSwitcherRow
            title={gettext('Weekly overtime')}
            subtitle={gettext('Track and prevent weekly overtime')}
            value={overtimeEnabledField.value}
            onChange={(value: boolean): void => onChangeHandler(value)}
          />
        }
        contentItem={
          <Row justifyContent="space-between" alignItems="center" pTop={20}>
            <Text14 themeColor={ThemeColorType.gray}>
              <TranslateWithComponents
                replaceComponents={{
                  overtimeWeekThreshold: (
                    <Text14 themeColor={ThemeColorType.primary}>
                      {displayNumberOrReplacer(overtimeWeekThresholdField.value, 'X')}
                    </Text14>
                  ),
                  overtimeWeekMultiplier: (
                    <Text14 themeColor={ThemeColorType.primary}>
                      {displayNumberOrReplacer(overtimeWeekMultiplierField.value, 'X')}
                    </Text14>
                  ),
                }}>
                {ngettext(
                  'Starts after [[overtimeWeekThreshold]] hour at rate [[overtimeWeekMultiplier]].',
                  'Starts after [[overtimeWeekThreshold]] hours at rate [[overtimeWeekMultiplier]].',
                  toNumber(overtimeWeekThresholdField.value)
                )}
              </TranslateWithComponents>
            </Text14>

            <Row alignItems="center">
              <CustomTextField
                width={FIELD_WIDTH_100}
                skipErrorMessage
                postfix={gettext('Hours')}
                value={overtimeWeekThresholdField.value}
                onChange={(event): void =>
                  overtimeWeekThresholdField.onChange(event.currentTarget.value)
                }
                invalid={!!overtimeWeekThresholdField.error}
                onBlur={overtimeWeekThresholdField.onBlur}
              />
              <Text14 themeColor={ThemeColorType.gray} pRight={10} pLeft={10} bold>
                {gettext('@ RATE X')}
              </Text14>

              <CustomTextField
                width={FIELD_WIDTH_100}
                skipErrorMessage
                value={overtimeWeekMultiplierField.value}
                onChange={(event): void => {
                  overtimeWeekMultiplierField.onChange(event.currentTarget.value);
                }}
                onBlur={overtimeWeekMultiplierField.onBlur}
                invalid={!!overtimeWeekMultiplierField.error}
              />
            </Row>
          </Row>
        }
      />
    </SettingsSection>
  );
};

export default weeklyOvertime;
